import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import { Icon } from 'leaflet';
import { Button } from './ui/button';
import { Alert, AlertDescription } from './ui/alert';
import { getCurrentPosition, formatDistance } from '../services/geolocation';
import { searchNearbyBusStops } from '../services/overpassApi';
import { Navigation, Loader2, List } from 'lucide-react';
import 'leaflet/dist/leaflet.css';

// 修復 Leaflet 默認圖標問題
delete Icon.Default.prototype._getIconUrl;
Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// 自定義圖標
const createCustomIcon = (color = 'blue') => {
  const svgString = `<svg width="25" height="41" viewBox="0 0 25 41" xmlns="http://www.w3.org/2000/svg">
    <path d="M12.5 0C5.6 0 0 5.6 0 12.5c0 12.5 12.5 28.5 12.5 28.5s12.5-16 12.5-28.5C25 5.6 19.4 0 12.5 0zm0 17c-2.5 0-4.5-2-4.5-4.5s2-4.5 4.5-4.5 4.5 2 4.5 4.5-2 4.5-4.5 4.5z" fill="${color}"/>
  </svg>`;
  
  return new Icon({
    iconUrl: `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgString)}`,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
  });
};

const userIcon = createCustomIcon('red');
const busStopIcon = createCustomIcon('blue');

// 地圖中心更新組件
const MapUpdater = ({ center, zoom }) => {
  const map = useMap();
  
  useEffect(() => {
    if (center) {
      map.setView(center, zoom);
    }
  }, [center, zoom, map]);
  
  return null;
};

const BusStopMap = ({ onBusStopSelect, selectedBusStop }) => {
  const [userLocation, setUserLocation] = useState(null);
  const [busStops, setBusStops] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchRadius, setSearchRadius] = useState(500);
  const [mapCenter, setMapCenter] = useState([25.0330, 121.5654]);
  const [mapZoom, setMapZoom] = useState(15);
  const mapRef = useRef(null);

  // 搜索附近的巴士站（使用 useCallback，避免在依賴變化時造成不必要的重建）
  const searchBusStops = useCallback(
    async (lat, lon) => {
      setLoading(true);
      setError(null);

      try {
        const stops = await searchNearbyBusStops(lat, lon, searchRadius);
        setBusStops(stops);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    },
    [searchRadius]
  );

  // 獲取用戶位置
  const handleGetLocation = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const position = await getCurrentPosition();
      setUserLocation(position);
      setMapCenter([position.latitude, position.longitude]);
      setMapZoom(16);

      await searchBusStops(position.latitude, position.longitude);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [searchBusStops]);

  // 處理巴士站點擊
  const handleBusStopClick = (busStop) => {
    if (onBusStopSelect) {
      onBusStopSelect(busStop);
    }
  };

  // 處理地圖點擊（防抖）
  const handleMapClick = (e) => {
    const { lat, lng } = e.latlng;
    setMapCenter([lat, lng]);
    
    if (mapRef.current?.debounceTimer) {
      clearTimeout(mapRef.current.debounceTimer);
    }
    
    mapRef.current.debounceTimer = setTimeout(() => {
      searchBusStops(lat, lng);
    }, 500);
  };

  useEffect(() => {
    handleGetLocation();
  }, [handleGetLocation]);

  useEffect(() => {
    if (userLocation) {
      searchBusStops(userLocation.latitude, userLocation.longitude);
    }
  }, [searchRadius, userLocation, searchBusStops]);

  useEffect(() => {
    const currentRef = mapRef.current;
    return () => {
      if (currentRef?.debounceTimer) {
        clearTimeout(currentRef.debounceTimer);
      }
    };
  }, []);

  return (
    <div className="w-full">
      {/* 頂部控制列（只保留控制按鈕與半徑顯示，不再顯示副標題文字） */}
      <div className="flex items-center justify-end mb-3 gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={handleGetLocation}
          disabled={loading}
          className="min-h-[36px]"
        >
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Navigation className="h-4 w-4" />
          )}
          <span className="ml-2 hidden sm:inline">定位</span>
        </Button>
        <span className="text-xs sm:text-sm text-muted-foreground px-2 py-1 bg-muted rounded">
          半徑: {searchRadius}m
        </span>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-3">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* 搜索半徑控制 */}
      <div className="mb-3 space-y-2">
        <label className="text-xs sm:text-sm font-medium">搜索半徑（公尺）</label>
        <div className="flex items-center gap-4">
          <input
            type="range"
            className="flex-1"
            min="100"
            max="1000"
            step="100"
            value={searchRadius}
            onChange={(e) => setSearchRadius(parseInt(e.target.value))}
          />
          <span className="text-xs sm:text-sm text-muted-foreground w-16 text-right">
            {searchRadius}m
          </span>
        </div>
        <div className="flex gap-2">
          {[200, 500, 1000].map((radius) => (
            <Button
              key={radius}
              variant={searchRadius === radius ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSearchRadius(radius)}
            >
              {radius}m
            </Button>
          ))}
        </div>
      </div>

      {/* 地圖容器：手機佔滿半頁，高度約 55vh，無額外卡片框 */}
      <div className="relative w-full h-[55vh] sm:h-[480px] rounded-xl overflow-hidden bg-white">
        <MapContainer
          center={mapCenter}
          zoom={mapZoom}
          style={{ height: '100%', width: '100%' }}
          ref={mapRef}
          onClick={handleMapClick}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          <MapUpdater center={mapCenter} zoom={mapZoom} />

          {/* 用戶位置標記 */}
          {userLocation && (
            <Marker
              position={[userLocation.latitude, userLocation.longitude]}
              icon={userIcon}
            >
              <Popup>
                <div>
                  <strong>您的位置</strong>
                  <br />
                  精度: {Math.round(userLocation.accuracy)}m
                  <br />
                  <small className="text-muted">
                    {new Date(userLocation.timestamp).toLocaleString()}
                  </small>
                </div>
              </Popup>
            </Marker>
          )}

          {/* 巴士站標記 */}
          {busStops.map((stop) => (
            <Marker
              key={stop.id}
              position={[stop.latitude, stop.longitude]}
              icon={busStopIcon}
              eventHandlers={{
                click: () => handleBusStopClick(stop),
              }}
            >
              <Popup>
                <div>
                  <h6 className="mb-2 font-semibold">{stop.name}</h6>
                  <p className="mb-1 text-sm">
                    距離: {formatDistance(stop.distance)}
                  </p>
                  {stop.tags.ref && (
                    <p className="mb-1 text-sm">
                      編號: {stop.tags.ref}
                    </p>
                  )}
                  {stop.tags.operator && (
                    <p className="mb-1 text-sm">
                      運營商: {stop.tags.operator}
                    </p>
                  )}
                  <div className="mt-2">
                    <Button
                      variant="default"
                      size="sm"
                      onClick={() => handleBusStopClick(stop)}
                    >
                      選擇此站
                    </Button>
                  </div>
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>

        {/* 選中巴士站資訊覆蓋層（顯示在地圖上） */}
        {selectedBusStop && (
          <div className="absolute inset-x-3 bottom-3 sm:inset-x-4 sm:bottom-4">
            <div className="rounded-xl bg-white/95 backdrop-blur-md shadow-lg p-3 sm:p-4 max-h-[45vh] overflow-y-auto">
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="font-semibold text-sm sm:text-base">
                  {selectedBusStop.name}
                </div>
                <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary">
                  {Math.round(selectedBusStop.distance)}m
                </span>
              </div>
              <div className="space-y-1 text-xs sm:text-sm text-muted-foreground">
                {selectedBusStop.tags.ref && (
                  <div>
                    <span className="font-medium">站台編號：</span>
                    {selectedBusStop.tags.ref}
                  </div>
                )}
                {selectedBusStop.tags.operator && (
                  <div>
                    <span className="font-medium">運營商：</span>
                    {selectedBusStop.tags.operator}
                  </div>
                )}
                {selectedBusStop.tags.route_ref && (
                  <div>
                    <span className="font-medium">路線：</span>
                    {selectedBusStop.tags.route_ref}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 巴士站列表（保留在地圖下方，可選擇性使用） */}
      {busStops.length > 0 && (
        <div className="mt-4">
          <h6 className="flex items-center gap-2 mb-3 font-semibold">
            <List className="h-4 w-4" />
            找到 {busStops.length} 個巴士站
          </h6>
          <div className="max-h-[200px] overflow-y-auto space-y-2">
            {busStops.map((stop) => (
              <div
                key={stop.id}
                className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                  selectedBusStop?.id === stop.id ? 'bg-primary/10 border-primary' : 'hover:bg-muted'
                }`}
                onClick={() => handleBusStopClick(stop)}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="font-medium">{stop.name}</div>
                    <div className="text-sm text-muted-foreground mt-1">
                      {formatDistance(stop.distance)}
                      {stop.tags.ref && (
                        <span className="ml-2">
                          #{stop.tags.ref}
                        </span>
                      )}
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleBusStopClick(stop);
                    }}
                  >
                    選擇
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center mt-4">
          <Loader2 className="h-4 w-4 animate-spin mr-2" />
          <span className="text-sm text-muted-foreground">
            搜索 {searchRadius}m 內的巴士站...
          </span>
        </div>
      )}
    </div>
  );
};

export default BusStopMap;
